import { Skeleton } from '@/components/ui/skeleton';
import { nombrePlantaDisplay } from '@/lib/plantas';
import type { M3PorDia } from '@adelante/shared';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

interface Props {
  datos: M3PorDia[];
  cargando?: boolean;
}

// Color principal: total (verde Adelante).
const VERDE_ADELANTE = '#ADD010';

// Colores por planta. Buscamos consistencia visual con el branding interno:
// la "Roja" en rojo/coral, la "Bianca" en azul (contrasta bien sobre verde).
//
// Cuando se agrega una 3era planta (Fiori u otra), agregar acá. El fallback
// `*` cubre cualquier planta sin color asignado.
const COLOR_POR_PLANTA: Record<string, string> = {
  'BLEND-1': '#DC2626', // Roja E50 → rojo
  'BLEND-2': '#2563EB', // Bianca E50 → azul
  'FIORI-1.8': '#F59E0B', // autocargable chica → ámbar
  'FIORI-4.0': '#059669', // autocargable grande → verde azulado
};
const COLOR_PLANTA_FALLBACK = '#7C3AED';

function colorParaPlanta(nombrePlanta: string): string {
  return COLOR_POR_PLANTA[nombrePlanta] ?? COLOR_PLANTA_FALLBACK;
}

const fmtMiles = new Intl.NumberFormat('es-CR', { maximumFractionDigits: 2 });

function formatearEtiquetaX(fechaIso: string): string {
  return format(parseISO(fechaIso), 'dd MMM', { locale: es });
}

/**
 * Aplanamos `m3_por_planta` para que recharts tenga acceso directo a cada
 * planta como dataKey. Resultado por fila:
 *   { fecha, m3, BLEND-1: 12.3, BLEND-2: 8.5 }
 *
 * Si una planta no tuvo producción ese día, su key NO se incluye → la línea
 * se "rompe" en ese punto (recharts lo dibuja como gap), lo cual es el
 * comportamiento esperado para días sin actividad.
 */
function aplanar(datos: M3PorDia[]) {
  return datos.map((d) => {
    const fila: Record<string, number | string> = {
      fecha: d.fecha,
      m3: d.m3,
    };
    for (const [planta, m3] of Object.entries(d.m3_por_planta ?? {})) {
      fila[planta] = m3;
    }
    return fila;
  });
}

interface FilaAplanada {
  fecha: string;
  m3: number;
  [planta: string]: number | string;
}

function TooltipPersonalizado({
  active,
  payload,
  plantasActivas,
}: TooltipProps<number, string> & { plantasActivas: string[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const fila = (payload[0] as unknown as { payload: FilaAplanada }).payload;
  if (!fila) return null;

  // Solo mostrar plantas con valor > 0 para no saturar el tooltip.
  const plantasConValor = plantasActivas.filter((p) => ((fila[p] as number | undefined) ?? 0) > 0);

  return (
    <div className="rounded-md border bg-background p-3 shadow-md">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {format(parseISO(fila.fecha), 'EEEE d MMM yyyy', { locale: es })}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">
        Total: {fmtMiles.format(fila.m3)} m³
      </div>
      {plantasConValor.length > 1 && (
        <div className="mt-1.5 space-y-0.5 text-xs">
          {plantasConValor.map((p) => {
            const valor = fila[p] as number;
            return (
              <div key={p} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: colorParaPlanta(p) }}
                />
                <span>{nombrePlantaDisplay(p)}:</span>
                <span className="font-mono tabular-nums">{fmtMiles.format(valor)} m³</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function GraficoM3PorDia({ datos, cargando = false }: Props) {
  const aplanados = useMemo(() => aplanar(datos), [datos]);

  // Detectamos qué plantas tuvieron producción en el rango. Si solo hay 1
  // (o 0), no tiene sentido mostrar líneas por planta — basta con el total.
  const plantasActivas = useMemo(() => {
    const set = new Set<string>();
    for (const d of datos) {
      for (const [p, m3] of Object.entries(d.m3_por_planta ?? {})) {
        if (m3 > 0) set.add(p);
      }
    }
    return Array.from(set).sort();
  }, [datos]);

  if (cargando) {
    return <Skeleton className="h-[280px] w-full" />;
  }

  const sinProduccion = datos.length === 0 || datos.every((d) => d.m3 === 0);
  if (sinProduccion) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        Sin producción registrada en este periodo
      </div>
    );
  }

  const mostrarPorPlanta = plantasActivas.length >= 2;

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={aplanados} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="fecha"
            tickFormatter={formatearEtiquetaX}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<TooltipPersonalizado plantasActivas={plantasActivas} />} />
          {mostrarPorPlanta && (
            <Legend
              verticalAlign="top"
              height={28}
              iconType="line"
              wrapperStyle={{ fontSize: 12 }}
            />
          )}

          {/* Línea TOTAL: siempre presente, gruesa y prominente. */}
          <Line
            type="monotone"
            dataKey="m3"
            stroke={VERDE_ADELANTE}
            strokeWidth={mostrarPorPlanta ? 3 : 2}
            dot={{ r: 3, fill: VERDE_ADELANTE }}
            activeDot={{ r: 5 }}
            name="Total"
          />

          {/* Líneas por planta: solo si hay >= 2 plantas activas. Más finas
              y semi-transparentes para que la TOTAL siga siendo el foco. */}
          {mostrarPorPlanta &&
            plantasActivas.map((p) => (
              <Line
                key={p}
                type="monotone"
                dataKey={p}
                stroke={colorParaPlanta(p)}
                strokeWidth={1.5}
                strokeOpacity={0.85}
                dot={{ r: 2, fill: colorParaPlanta(p) }}
                activeDot={{ r: 4 }}
                name={nombrePlantaDisplay(p)}
                connectNulls={false}
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
